import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import * as actions from '../../../../../store/index';
import { highDensity } from '../../../../../helpers/density';
import SummaryParagraphHeader from '../SummaryParagraphHeader';
import { getCurrentModuleStatus } from '../SummaryCommon/SummaryCommon';
import { ReviewerSelector } from '../SummaryCommon/ReviewerSelector';
import {
  Grid,
  Avatar,
  Tooltip,
  Typography,
  Divider,
  CardHeader,
  CardContent,
  Card,
  Button,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { SummaryCardsActions } from '../SummaryCommon/ModuleCardsActions';
import { densityClassColor } from '../../../../../helpers/manageColors';

const useStyles = makeStyles((theme) => ({
  projections_density: {
    position: 'absolute',
    display: 'flex',
    height: '50%',
    width: '50%',
    color: theme.palette.background.mainDark,
    justifyContent: 'center',
    alignItems: 'center',
  },

  projDensityText: {
    fontSize: '1.2rem',
    margin: '5% 20%',
    color: 'white',
  },

  tooltip_text: {
    letterSpacing: '0.09rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  reviewer_chip_small: {
    marginLeft: '5px',
    minWidth: '45px',
    maxWidth: '40%',
  },

  reviewer_chip_big: {
    marginLeft: '10px',
    minWidth: '70px',
  },

  card_main_container: {
    margin: '0',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));

export default function DensitySummary(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles();

  // Store state
  const results = useSelector((state) => state.results);
  const { landscape } = useSelector((store) => store.gui);
  const { applied_density, confirmed_density, shown_density } = results;
  const { premium_features, user_settings } = useSelector((state) => state.global_settings);
  const { inverted_view_position } = user_settings || {};

  const [densityPopperOpen, setDensityPopperOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isConfirmed = applied_density === 'bbox' && confirmed_density;
  const isRelabeled = confirmed_density && applied_density !== 'bbox';

  const shownDensity = shown_density || applied_density;
  const { diagnostics_microcalc_module, diagnostics_opacities_module, data_annotation } = premium_features;
  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';
  const diagnosticsModule =
    (diagnostics_microcalc_module || diagnostics_opacities_module) &&
    localStorage.getItem('diagnostics') === 'true';
  const moduleStatus = getCurrentModuleStatus(
    applied_density,
    shownDensity,
    isConfirmed,
    isRelabeled,
    t('ResultView.density'),
    theme,
    t,
  );
  const densityMap = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
  };

  // Effects
  const compareDensityClasses = (densityProj) => {
    const rightProjections = ['rcc', 'rmlo'];
    const leftProjections = ['lcc', 'lmlo'];

    const getMaxDensity = (projections) => {
      let maxDensity = null;

      projections.forEach((projection) => {
        if (densityProj?.[projection]?.DensClass) {
          const densClassValue = densityMap[densityProj[projection].DensClass];
          if (maxDensity === null || densClassValue > maxDensity) {
            maxDensity = densClassValue;
          }
        }
      });
      return maxDensity;
    };

    const maxRightDensity = getMaxDensity(rightProjections);
    const maxLeftDensity = getMaxDensity(leftProjections);

    if (maxRightDensity === null && maxLeftDensity === null) {
      return null;
    }
    if (maxRightDensity === null || maxLeftDensity === null) {
      return null;
    }
    const densityDifference = Math.abs(maxRightDensity - maxLeftDensity);

    if (densityDifference === 0) {
      return null;
    } else if (densityDifference === 1) {
      return 1;
    } else {
      return 2;
    }
  };

  useEffect(() => {
    setDensityPopperOpen(false);
  }, []);

  const handleEditButtonClick = () => {
    dispatch(actions.setTabsIndex(2));
    dispatch(actions.setDensityInRevisionMode(true));
  };

  const handleTitleClick = () => {
    dispatch(actions.setTabsIndex(2));
    dispatch(actions.setDensityInRevisionMode(true));
  };

  const onConfirmClick = () => {
    const state = shown_density === applied_density && confirmed_density;

    dispatch(actions.confirmEvaluation(shownDensity, 'density', !state, t));
  };

  let density;
  if (!props.zoomedProjection) density = highDensity(props.results);
  else
    density =
      props.results?.[props.zoomedProjection]?.DensClass || props.results?.[props.zoomedProjection]?.DensMax;

  return (
    <Card
      className={classes.card_main_container}
      style={{
        height: '100%',
      }}>
      <CardHeader
        style={{
          padding: '0',
          backgroundColor: theme.palette.texts.generalTitleBackgrounds,
          height: '38px',
        }}
        title={
          <div
            style={{
              display: 'flex',
              width: '100%',
              padding: '0px 8px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <SummaryParagraphHeader
              header={t('b_box.b_density')}
              onClick={() => annotatorRole && handleTitleClick()}
              tootlipTitle={t('ResultView.view_more_density')}
            />
            <div className={props.diagnosticsShown ? classes.reviewer_chip_small : classes.reviewer_chip_big}>
              <ReviewerSelector
                type='density'
                shownContent={shownDensity}
                appliedContent={applied_density}
                isConfirmed={isConfirmed}
                isRelabeled={isRelabeled}
                classes={classes}
                onPopperClose={() => setDensityPopperOpen(false)}
              />
            </div>
          </div>
        }
      />

      <CardContent
        style={{
          padding: '0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'column',
          position: 'relative',
        }}>
        {compareDensityClasses(props.results) && compareDensityClasses(props.results) !== 0 && (
          <Tooltip
            arrow
            title={
              <Typography className={classes.tooltip_text}>{t('ResultView.density_asymmetry')}</Typography>
            }>
            <WarningIcon
              fontSize='small'
              style={{
                cursor: 'help',
                position: 'absolute',
                right: '10px',
                top: '4px',
                fill: compareDensityClasses(props.results) === 1 ? '#ff9800' : '#e74034',
              }}
            />
          </Tooltip>
        )}

        <Grid
          item
          xs={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}>
          <Tooltip
            title={
              <Typography className={classes.tooltip_text}>{`${t(
                'ResultView.overall_density',
              )}: ${density ? density : 'N/A'}`}</Typography>
            }
            arrow>
            <div
              style={{
                position: 'relative',
                width: !landscape ? '9vh' : diagnosticsModule ? '10vh' : '11vh',
                height: !landscape ? '9vh' : diagnosticsModule ? '10vh' : '11vh',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: annotatorRole ? 'pointer' : 'default',
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => annotatorRole && handleTitleClick()}>
              {isHovered ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textTransform: 'uppercase',
                  }}>
                  {(() => {
                    const topLeft = inverted_view_position ? 'rmlo' : 'rcc';
                    const topRight = inverted_view_position ? 'lmlo' : 'lcc';
                    const bottomLeft = inverted_view_position ? 'rcc' : 'rmlo';
                    const bottomRight = inverted_view_position ? 'lcc' : 'lmlo';
                    return (
                      <>
                        <div
                          style={{
                            background: `${densityClassColor(props.results?.[topLeft]?.DensClass, theme)}50`,
                            top: 0,
                            left: 0,
                            borderBottom: `1px solid ${theme.palette.background.mainDark}`,
                            borderRight: `1px solid ${theme.palette.background.mainDark}`,
                          }}
                          className={classes.projections_density}>
                          <p className={classes.projDensityText}>{props.results?.[topLeft]?.DensClass}</p>
                        </div>
                        <div
                          style={{
                            background: `${densityClassColor(props.results?.[topRight]?.DensClass, theme)}50`,
                            top: 0,
                            right: 0,
                            borderBottom: `1px solid ${theme.palette.background.mainDark}`,
                          }}
                          className={classes.projections_density}>
                          <p className={classes.projDensityText}>{props.results?.[topRight]?.DensClass}</p>
                        </div>
                        <div
                          style={{
                            background: `${densityClassColor(
                              props.results?.[bottomLeft]?.DensClass,
                              theme,
                            )}50`,
                            bottom: 0,
                            left: 0,
                            borderRight: `1px solid ${theme.palette.background.mainDark}`,
                          }}
                          className={classes.projections_density}>
                          <p className={classes.projDensityText}>{props.results?.[bottomLeft]?.DensClass}</p>
                        </div>
                        <div
                          style={{
                            background: `${densityClassColor(
                              props.results?.[bottomRight]?.DensClass,
                              theme,
                            )}50`,
                            bottom: 0,
                            right: 0,
                          }}
                          className={classes.projections_density}>
                          <p className={classes.projDensityText}>{props.results?.[bottomRight]?.DensClass}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div
                  style={{
                    background: `${densityClassColor(density, theme)}50`,
                    width: '100%',
                    height: '100%',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <div
                    style={{
                      fontSize: '3.5rem',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}>
                    {density}
                  </div>
                </div>
              )}
            </div>
          </Tooltip>
        </Grid>
      </CardContent>

      <SummaryCardsActions
        isConfirmed={isConfirmed}
        isRelabeled={isRelabeled}
        annotatorRole={annotatorRole}
        moduleStatus={moduleStatus}
        handleEditButtonClick={handleEditButtonClick}
        onConfirmClick={onConfirmClick}
        module='density'
        hasResults={!!props?.results}
      />
    </Card>
  );
}
